import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { LGoBadge, LGoButton } from "@/ui/branded";
import { CustomFieldAnswers } from "@/features/custom-fields/components/CustomFieldAnswers";
import type { PartnerReservation } from "@/domain/reservation/types";
import { formatTime } from "@/lib/time";
import { TodaySection } from "./TodaySection";

interface ArrivalsTodayProps {
  arrivals: PartnerReservation[];
}

export function ArrivalsToday({ arrivals }: ArrivalsTodayProps) {
  return (
    <TodaySection
      icon={<Users className="h-4 w-4 text-primary" aria-hidden="true" />}
      title="Dnes dorazí"
      count={arrivals.length}
      explanation="Zaplacené rezervace na dnešní termíny — i s tím, co u nich zákazník vyplnil."
    >
      {arrivals.length === 0 ? (
        <p className="text-sm text-muted-foreground">Dnes nikdo nedorazí.</p>
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {arrivals.map((arrival) => (
            <li
              key={arrival.id}
              className="flex flex-col gap-2 py-5 md:flex-row md:items-start md:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium truncate">{arrival.activityName}</p>
                  <LGoBadge variant={arrival.validatedAt ? "success" : "neutral"}>
                    {arrival.validatedAt ? "Dorazil" : "Příchod zatím nezaznamenán"}
                  </LGoBadge>
                </div>
                <p className="text-sm text-muted-foreground tabular-nums">
                  {arrival.startTime ? `${formatTime(arrival.startTime)} · ` : ""}
                  {arrival.customerName ?? "Zákazník"}
                  {arrival.code ? ` · ${arrival.code}` : ""}
                </p>
                {arrival.customAnswers.length > 0 && (
                  <CustomFieldAnswers answers={arrival.customAnswers} />
                )}
              </div>
              {arrival.code && !arrival.validatedAt && (
                <LGoButton asChild variant="secondary" size="lg" className="shrink-0 self-start">
                  <Link
                    to={`/validate?kod=${encodeURIComponent(arrival.code)}`}
                    aria-label={`Ověřit kód: ${arrival.customerName ?? "zákazník"}, ${arrival.activityName}`}
                  >
                    Ověřit kód
                  </Link>
                </LGoButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </TodaySection>
  );
}
